import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { Row, Col } from "react-bootstrap";
import { PrintContextConsumer } from "react-to-print";

import errorToast from "helpers/errorToast";
import { getRequest } from "api/axiosClient";
import CardNew from "components/molecules/Card/CardNew";
import testIds from "constants/testIds";
import { getAllAttachments } from "api/services/shipment";
import { downloadAllQuickRequestAttachments } from "api";

function QuoteAttachments({ data, isQuickRequest, hideDownloadButton }) {
  const { REACT_APP_CARGOBASE_BASE_URL } = import.meta.env;

  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const shipmentId = isQuickRequest ? data?.organization?.quick_request_reference : data?.organization?.shipment_id;

  const getAttachments = () => {
    getRequest(`${REACT_APP_CARGOBASE_BASE_URL}/request/${shipmentId}/attachments`)
      .then((response) => {
        const bucketAttachments = response.data.bucket_attachments;
        const otherAttachments = response.data.other_attachments;
        setAttachments([...formatBucketData(bucketAttachments), ...formatBucketData(otherAttachments)]);
        setIsLoading(false);
      })
      .catch((error) => errorToast(error));
  };

  const downloadAllAttachments = () => {
    const requestFn = isQuickRequest ? downloadAllQuickRequestAttachments : getAllAttachments;
    requestFn(shipmentId)
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/zip" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `attachments-${shipmentId}.zip`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      })
      .catch((error) => errorToast(error));
  };

  useEffect(() => {
    if (!shipmentId) return;
    getAttachments();
  }, [shipmentId]);

  /**
   * bucket_attachments & other_attachments have different structure
   * so this function will format them before saving to state for consistency.
   */
  function formatBucketData(data) {
    return data.map((item) => ({
      id: item?.file?.id || item?.id,
      bucket_name: item?.attachment_bucket?.bucket_name || item?.bucket_name || "Attachment",
      url: item?.file?.url || item?.url,
      name: item?.file?.name || item?.name,
    }));
  }

  return (
    <CardNew
      className="mb-15 font-b3 hide-during-print"
      id="customs-div"
      isLoading={isLoading}
      title={<FormattedMessage id="quick-request-detail.attachments" defaultMessage="Attachments" />}
    >
      {attachments.length > 0 ? (
        attachments.map((attachment) => (
          <Row className="mb-2" key={attachment.id}>
            <Col className="text-secondary">{attachment.bucket_name}</Col>
            <Col>
              <a target="_blank" href={attachment.url} rel="noopener noreferrer">
                {attachment.name}
              </a>
            </Col>
          </Row>
        ))
      ) : (
        <div className="text-center text-secondary my-2">
          <FormattedMessage id="quote-request-detail.noAttachmentsFound" defaultMessage="No attachments found" />
        </div>
      )}
      {attachments.length > 0 && (
        <a className="download-button text-center my-1 mt-2" role="button" tabIndex={0} onClick={downloadAllAttachments}>
          <FormattedMessage id="tracking.downloadAllAttachments" defaultMessage="Download all attachments" />
        </a>
      )}
      {!hideDownloadButton && (
        <PrintContextConsumer>
          {({ handlePrint }) => (
            <div
              className="download-button text-center my-1"
              onClick={handlePrint}
              data-testid={testIds.SHIPMENT_DASHBOARD_DOWNLOAD_DETAILS_AS_PDF_BUTTON}
              role="button"
              tabIndex={0}
            >
              <FormattedMessage id="tracking.downloadDetailsAsPDF" defaultMessage="Download details as PDF" />
            </div>
          )}
        </PrintContextConsumer>
      )}
    </CardNew>
  );
}

QuoteAttachments.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  isQuickRequest: PropTypes.bool,
  hideDownloadButton: PropTypes.bool,
};

QuoteAttachments.defaultProps = {
  isQuickRequest: false,
  hideDownloadButton: false,
};

export default QuoteAttachments;
